import type { MediaFile } from './patient-media.types.ts';

function hashNum(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function fmtDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function addDays(date: string, days: number): string {
  const parts = date.split('-').map(Number) as [number, number, number];
  const d = new Date(parts[0], parts[1] - 1, parts[2] + days);
  return fmtDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

const PHOTO_NAMES: { name: string; label: string }[] = [
  { name: 'Face-repos', label: 'Face repos' },
  { name: 'Face-sourire', label: 'Face sourire' },
  { name: 'Profil-droit', label: 'Profil droit' },
  { name: 'Profil-gauche', label: 'Profil gauche' },
  { name: 'Intraoral-face', label: 'Intraoral face' },
  { name: 'Occlusal-max', label: 'Occlusal max.' },
  { name: 'Occlusal-mandi', label: 'Occlusal mandi.' },
];

/** Returns flat list of all patient files — no folder hierarchy. */
export function getPatientFiles(uuid: string): MediaFile[] {
  const h = hashNum(uuid);
  const files: MediaFile[] = [];

  const baseYear = 2022 + (h % 3);
  const baseDate = fmtDate(baseYear, (h % 10) + 1, (h % 20) + 1);
  const followDate = addDays(baseDate, 150 + (h % 60));

  // ── Radiographies (PNG) ──────────────────────────────────────────────────
  files.push({
    id: `${uuid}-r-pan`,
    name: 'Panoramique',
    type: 'png',
    size: 4521890 + (h % 1500000),
    date: baseDate,
  });
  if (h % 2 === 0) {
    files.push({
      id: `${uuid}-r-tele-face`,
      name: 'Télé-de-face',
      type: 'png',
      size: 3892130,
      date: baseDate,
    });
    files.push({
      id: `${uuid}-r-tele-profil`,
      name: 'Téléradiographie-profil',
      type: 'png',
      size: 3200000 + (h % 800000),
      date: baseDate,
    });
  }
  if (h % 3 !== 1) {
    files.push({
      id: `${uuid}-r-pan-2`,
      name: 'Panoramique-suivi',
      type: 'png',
      size: 4800000 + (h % 1000000),
      date: followDate,
    });
  }

  // ── Photos cliniques (JPEG) ──────────────────────────────────────────────
  const photoCount = 3 + (h % 5);
  for (let i = 0; i < Math.min(photoCount, PHOTO_NAMES.length); i++) {
    files.push({
      id: `${uuid}-p-${i}`,
      name: PHOTO_NAMES[i]!.name,
      type: 'jpeg',
      size: 1800000 + ((h + i * 7) % 1200000),
      date: baseDate,
    });
  }
  if (h % 2 === 1) {
    for (let i = 0; i < 3; i++) {
      files.push({
        id: `${uuid}-p2-${i}`,
        name: `${PHOTO_NAMES[i]!.name}-suivi`,
        type: 'jpeg',
        size: 1900000 + ((h + i * 11) % 900000),
        date: followDate,
      });
    }
  }

  // ── Modèles 3D (STL) ────────────────────────────────────────────────────
  if (h % 3 !== 0) {
    files.push({
      id: `${uuid}-m-maxi`,
      name: 'Arcade-maxillaire-T0',
      type: 'stl',
      size: 8923456,
      date: baseDate,
    });
    files.push({
      id: `${uuid}-m-mandi`,
      name: 'Arcade-mandibulaire-T0',
      type: 'stl',
      size: 7834512,
      date: baseDate,
    });
    if (h % 3 !== 2) {
      files.push({
        id: `${uuid}-m-maxi2`,
        name: 'Arcade-maxillaire-T6',
        type: 'stl',
        size: 9012345,
        date: followDate,
      });
      files.push({
        id: `${uuid}-m-mandi2`,
        name: 'Arcade-mandibulaire-T6',
        type: 'stl',
        size: 8234567,
        date: followDate,
      });
    }
  }

  // ── Vidéos (MP4) ────────────────────────────────────────────────────────
  if (h % 4 !== 0) {
    files.push({
      id: `${uuid}-v-1`,
      name: 'Evolution-traitement',
      type: 'mp4',
      size: 45678901 + (h % 20000000),
      date: followDate,
    });
    if (h % 3 === 0) {
      files.push({
        id: `${uuid}-v-2`,
        name: 'Demonstration-elastiques',
        type: 'mp4',
        size: 18234567,
        date: followDate,
      });
    }
  }

  // ── Documents (PDF) ──────────────────────────────────────────────────────
  files.push({
    id: `${uuid}-d-plan`,
    name: 'Plan-traitement-complet',
    type: 'pdf',
    size: 892345,
    date: baseDate,
  });
  files.push({
    id: `${uuid}-d-devis`,
    name: 'Devis',
    type: 'pdf',
    size: 456789,
    date: baseDate,
  });
  files.push({
    id: `${uuid}-d-consent`,
    name: 'Consentement-eclaire',
    type: 'pdf',
    size: 234567,
    date: baseDate,
  });
  if (h % 3 !== 1) {
    files.push({
      id: `${uuid}-d-cr`,
      name: 'Compte-rendu-bilan',
      type: 'pdf',
      size: 1023456,
      date: baseDate,
    });
  }

  // Sort: PNG first, then JPEG, STL, MP4, PDF
  const order: Record<string, number> = {
    png: 0,
    jpeg: 1,
    stl: 2,
    mp4: 3,
    pdf: 4,
  };
  return files.sort((a, b) => (order[a.type] ?? 9) - (order[b.type] ?? 9));
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function formatFileDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
